import { createSlice, nanoid } from "@reduxjs/toolkit";

import type { MoodboardJson, PortalThemeTokens } from "@pages/portal-lab/renderer/types/portal";
import type { PayloadAction } from "@reduxjs/toolkit";

import {
    PORTAL_ELEMENT_ITEMS,
    PORTAL_FONT_OPTIONS,
    getPortalBlockIdFromElementType,
    getPortalElementTypeFromBlockId,
    resolvePortalFontUrl,
    type PortalCornerRadius,
    type PortalElementType,
    type PortalPageTemplate,
    type PortalElementBlockId,
} from "@utils/constants/portalLab";
import type { Pipeline } from "@utils/types/integration";
import {
    COMPANY_PORTAL_STAGE_BUILD_FAILED,
    COMPANY_PORTAL_STAGE_BUILD_FINISHED,
    COMPANY_PORTAL_STAGE_BUILD_PROCESSING,
    COMPANY_PORTAL_STAGE_DEPLOYMENT_FAILED,
    COMPANY_PORTAL_STAGE_DEPLOYMENT_FINISHED,
    COMPANY_PORTAL_STAGE_DEPLOYMENT_PROCESSING,
    COMPANY_PORTAL_STAGE_PORTAL_READY,
    COMPANY_PORTAL_STAGE_STEP2_FAILED,
    COMPANY_PORTAL_STAGE_STEP2_FINISHED,
    COMPANY_PORTAL_STAGE_STEP2_PROCESSING,
    COMPANY_PORTAL_STAGE_STEP3_FAILED,
    COMPANY_PORTAL_STAGE_STEP3_FINISHED,
    COMPANY_PORTAL_STAGE_STEP3_PROCESSING,
    type PortalLabCompanyPortalProcessingTemplateResponse,
} from "@service/portalLabApi";

/** Wizard step 3 (Deploy) — build / deployment / portal-ready stages. */
function isPortalLabDeployWizardStage(stage: string | null | undefined): boolean {
    if (!stage?.trim()) {
        return false;
    }

    const normalized = stage.trim().toLowerCase();

    return (
        normalized === COMPANY_PORTAL_STAGE_STEP3_PROCESSING.toLowerCase() ||
        normalized === COMPANY_PORTAL_STAGE_STEP3_FAILED.toLowerCase() ||
        normalized === COMPANY_PORTAL_STAGE_STEP3_FINISHED.toLowerCase() ||
        normalized === COMPANY_PORTAL_STAGE_BUILD_PROCESSING.toLowerCase() ||
        normalized === COMPANY_PORTAL_STAGE_BUILD_FAILED.toLowerCase() ||
        normalized === COMPANY_PORTAL_STAGE_BUILD_FINISHED.toLowerCase() ||
        normalized === COMPANY_PORTAL_STAGE_DEPLOYMENT_PROCESSING.toLowerCase() ||
        normalized === COMPANY_PORTAL_STAGE_DEPLOYMENT_FAILED.toLowerCase() ||
        normalized === COMPANY_PORTAL_STAGE_DEPLOYMENT_FINISHED.toLowerCase() ||
        normalized === COMPANY_PORTAL_STAGE_PORTAL_READY.toLowerCase() ||
        normalized.includes("deployment") ||
        normalized.includes("build")
    );
}

function isPortalLabPreviewWizardStage(stage: string | null | undefined): boolean {
    if (!stage?.trim()) {
        return false;
    }

    const normalized = stage.trim().toLowerCase();

    return (
        normalized === COMPANY_PORTAL_STAGE_STEP2_PROCESSING.toLowerCase() ||
        normalized === COMPANY_PORTAL_STAGE_STEP2_FAILED.toLowerCase() ||
        normalized === COMPANY_PORTAL_STAGE_STEP2_FINISHED.toLowerCase()
    );
}

function applyPortalLabWizardStepFromStage(
    state: { completedStep: number; currentStep: number },
    stage: string | null | undefined,
    previousStage: string | null | undefined
): void {
    // Advance only when *entering* a stage band (e.g. preview → deploy). Re-applying
    // the same deploy stage must not pin the user on step 3, or Edit Preview / stepper
    // navigation immediately snaps back.
    if (isPortalLabDeployWizardStage(stage)) {
        state.completedStep = Math.max(state.completedStep, 3);
        const enteringDeploy = !isPortalLabDeployWizardStage(previousStage);
        if (enteringDeploy && state.currentStep < 3) {
            state.currentStep = 3;
        }
        return;
    }

    if (isPortalLabPreviewWizardStage(stage)) {
        state.completedStep = Math.max(state.completedStep, 1);
        const enteringPreview =
            !isPortalLabPreviewWizardStage(previousStage) &&
            !isPortalLabDeployWizardStage(previousStage);
        if (enteringPreview && state.currentStep < 2) {
            state.currentStep = 2;
        }
    }
}

const VALID_PREVIEW_BLOCK_IDS = new Set(
    PORTAL_ELEMENT_ITEMS.map((item) => item.id)
);

const VALID_PORTAL_ELEMENT_TYPES = new Set<string>([
    "navigation_menu",
    "banner_hero",
    "liveboard_area",
    "full_chat_agent",
    "ai_chat_widget",
    "kpi_block",
    "specific_chart",
    "content_block",
    "image",
]);

function parsePortalElementType(raw: unknown): PortalElementType {
    if (typeof raw === "string" && VALID_PORTAL_ELEMENT_TYPES.has(raw)) {
        return raw as PortalElementType;
    }
    return "content_block";
}

function parseCornerRadius(raw: unknown, fallback: PortalCornerRadius): PortalCornerRadius {
    if (raw === "none" || raw === "s" || raw === "m" || raw === "l") {
        return raw;
    }
    return fallback;
}

/** Used when API sends `colors: null` or per-channel nulls. */
const DEFAULT_PORTAL_BRAND_COLORS: PortalBrandColors = {
    primary: "#059669",
    secondary: "#0F766E",
    dark: "#1F2937",
    light: "#ECFDF5",
    accent: "#F97316",
};

function coalesceNullBrandColorSlots(colors: PortalBrandColors): PortalBrandColors {
    const next = { ...colors };
    (["primary", "secondary", "dark", "light", "accent"] as const).forEach((k) => {
        if (next[k] === null) {
            next[k] = DEFAULT_PORTAL_BRAND_COLORS[k];
        }
    });
    return next;
}

function parseBrandColorsFromServer(
    raw: unknown,
    fallback: PortalBrandColors
): PortalBrandColors {
    const keys: PortalBrandColorKey[] = [
        "primary",
        "secondary",
        "dark",
        "light",
        "accent",
    ];

    if (Array.isArray(raw)) {
        const next: PortalBrandColors = {
            ...DEFAULT_PORTAL_BRAND_COLORS,
        };

        raw.forEach((item, index) => {
            if (
                item &&
                typeof item === "object" &&
                !Array.isArray(item)
            ) {
                const hex = (item as Record<string, unknown>).hex;

                if (
                    typeof hex === "string" &&
                    hex.trim() &&
                    keys[index]
                ) {
                    next[keys[index]] = hex.trim();
                }
            }
        });

        return next;
    }

    if (raw === null) {
        return { ...DEFAULT_PORTAL_BRAND_COLORS };
    }

    if (raw !== null && typeof raw === "object" && !Array.isArray(raw)) {
        const o = raw as Record<string, unknown>;
        const next: PortalBrandColors = { ...fallback };

        keys.forEach((k) => {
            const v = o[k];

            if (typeof v === "string" && v.trim()) {
                next[k] = v.trim();
            } else if (v === null) {
                next[k] = DEFAULT_PORTAL_BRAND_COLORS[k];
            }
        });

        return coalesceNullBrandColorSlots(next);
    }

    return coalesceNullBrandColorSlots({ ...fallback });
}

function serverFontToPortalFont(font: unknown, fallback: string): string {
    if (typeof font !== "string") {
        return fallback;
    }
    const byId = PORTAL_FONT_OPTIONS.find((opt) => opt.id === font);
    if (byId) {
        return byId.name;
    }
    if (PORTAL_FONT_OPTIONS.some((opt) => opt.name === font)) {
        return font;
    }
    return fallback;
}

function parsePositiveIntFromCompanyPortalField(raw: unknown): number | null {
    if (typeof raw === "number" && Number.isFinite(raw)) {
        return raw;
    }
    if (typeof raw === "string" && /^\d+$/.test(raw.trim())) {
        return parseInt(raw.trim(), 10);
    }
    return null;
}

function parseConnectionIdFromCompanyPortalState(connectionDetails: unknown): number | null {
    if (
        connectionDetails === null ||
        typeof connectionDetails !== "object" ||
        Array.isArray(connectionDetails)
    ) {
        return null;
    }
    const o = connectionDetails as Record<string, unknown>;
    return parsePositiveIntFromCompanyPortalField(o.connection_id) ??
        parsePositiveIntFromCompanyPortalField(o.id);
}

function parseConnectionTextFromCompanyPortalState(
    connectionDetails: unknown,
    keys: string[]
): string | null {
    if (
        connectionDetails === null ||
        typeof connectionDetails !== "object" ||
        Array.isArray(connectionDetails)
    ) {
        return null;
    }
    const o = connectionDetails as Record<string, unknown>;
    for (const key of keys) {
        const raw = o[key];
        if (typeof raw === "string" && raw.trim()) {
            return raw.trim();
        }
    }
    return null;
}

function pipelineContainsConnectionId(pipeline: Pipeline, connectionId: number): boolean {
    return pipeline.connections_pipeline.some((c) => Number(c) === connectionId);
}

/** `ProcessingState.deployment_url` from company-portal entity; `"omit"` if key absent (keep slice value). */
function parseDeploymentUrlFromCompanyPortalEmbedded(
    embedded: Record<string, unknown>
): "omit" | string | null {
    if (!("deployment_url" in embedded)) {
        return "omit";
    }
    const raw = embedded["deployment_url"];
    if (raw === null || raw === undefined) {
        return null;
    }
    if (typeof raw === "string") {
        const t = raw.trim();
        return t.length > 0 ? t : null;
    }
    return null;
}

function readTrimmedPortalStringField(
    source: Record<string, unknown>,
    keys: string[]
): string | null | "omit" {
    let sawNull = false;

    for (const key of keys) {
        if (!(key in source)) {
            continue;
        }

        const raw = source[key];

        if (typeof raw === "string") {
            const trimmed = raw.trim();
            return trimmed.length > 0 ? trimmed : "";
        }

        if (raw === null) {
            sawNull = true;
        }
    }

    return sawNull ? null : "omit";
}

const PORTAL_THEME_MODES = ["auto", "light", "dark"] as const;
const PORTAL_THEME_DENSITIES = ["compact", "comfortable", "spacious"] as const;
const PORTAL_THEME_CARD_STYLES = ["flat", "soft", "elevated", "outlined", "glass", "gradient_border", "glow"] as const;
const PORTAL_THEME_SURFACE_PATTERNS = ["none", "soft_gradient", "mesh", "dotted_grid", "aurora"] as const;
const PORTAL_THEME_HERO_STYLES = ["overlay", "gradient_mesh", "split", "minimal", "aurora"] as const;
const PORTAL_THEME_NAV_STYLES = ["glass", "solid", "floating", "bordered"] as const;
const PORTAL_THEME_MOTIONS = ["none", "subtle", "lively"] as const;

export const DEFAULT_PORTAL_THEME: PortalThemeTokens = {
    mode: "auto",
    density: "comfortable",
    card_style: "soft",
    surface_pattern: "none",
    hero_style: "overlay",
    nav_style: "glass",
    motion: "subtle",
    chart_palette: [],
    accent_intensity: 70,
    surface_tint: 12,
};

function parsePortalTheme(raw: unknown, fallback: PortalThemeTokens): PortalThemeTokens {
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
        return fallback;
    }
    const r = raw as Record<string, unknown>;
    const pick = <T extends string>(value: unknown, allowed: readonly T[], fb: T): T =>
        typeof value === "string" && (allowed as readonly string[]).includes(value) ? (value as T) : fb;
    const intIn = (value: unknown, fb: number): number =>
        typeof value === "number" && Number.isFinite(value) ? value : fb;
    return {
        mode: pick(r.mode, PORTAL_THEME_MODES, fallback.mode),
        density: pick(r.density, PORTAL_THEME_DENSITIES, fallback.density),
        card_style: pick(r.card_style, PORTAL_THEME_CARD_STYLES, fallback.card_style),
        surface_pattern: pick(r.surface_pattern, PORTAL_THEME_SURFACE_PATTERNS, fallback.surface_pattern),
        hero_style: pick(r.hero_style, PORTAL_THEME_HERO_STYLES, fallback.hero_style),
        nav_style: pick(r.nav_style, PORTAL_THEME_NAV_STYLES, fallback.nav_style),
        motion: pick(r.motion, PORTAL_THEME_MOTIONS, fallback.motion),
        chart_palette: Array.isArray(r.chart_palette)
            ? r.chart_palette.filter((c): c is string => typeof c === "string")
            : fallback.chart_palette,
        accent_intensity: intIn(r.accent_intensity, fallback.accent_intensity),
        surface_tint: intIn(r.surface_tint, fallback.surface_tint),
    };
}

function mergeCompanyPortalServerPayload(
    raw: Record<string, unknown>,
    prev: PortalConfig
): PortalConfig {
    const prevBk = prev.brand_kit;

    const pagesRaw = raw.pages;
    let pages: PortalLabPageTab[] = Array.isArray(pagesRaw)
        ? pagesRaw.map((p, pageIdx) => {
            const po = p && typeof p === "object" && !Array.isArray(p) ? (p as Record<string, unknown>) : {};
            const rowsRaw = po.rows;
            const rows: PortalLabPageRow[] = Array.isArray(rowsRaw)
                ? rowsRaw.map((r) => {
                    const ro =
                        r && typeof r === "object" && !Array.isArray(r)
                            ? (r as Record<string, unknown>)
                            : {};
                    const elsRaw = ro.elements;
                    const elements: PortalLabPageElement[] = Array.isArray(elsRaw)
                        ? elsRaw.map((el) => {
                            const eo =
                                el && typeof el === "object" && !Array.isArray(el)
                                    ? (el as Record<string, unknown>)
                                    : {};
                            const cfg = eo.config;
                            const config: Record<string, unknown> =
                                cfg !== null &&
                                    typeof cfg === "object" &&
                                    !Array.isArray(cfg)
                                    ? (cfg as Record<string, unknown>)
                                    : {};
                            const spanNum = eo.span;
                            const span =
                                typeof spanNum === "number" &&
                                    Number.isFinite(spanNum) &&
                                    spanNum >= 1 &&
                                    spanNum <= 12
                                    ? spanNum
                                    : 12;
                            const idStr = eo.id;
                            const heightRaw = eo.height_px;
                            const height_px =
                                typeof heightRaw === "number" && Number.isFinite(heightRaw) ? heightRaw : null;
                            return {
                                id: typeof idStr === "string" && idStr.length > 0 ? idStr : nanoid(),
                                type: parsePortalElementType(eo.type),
                                span,
                                height_px,
                                config,
                            };
                        })
                        : [];
                    const rid = ro.id;
                    return {
                        id: typeof rid === "string" && rid.length > 0 ? rid : nanoid(),
                        elements,
                    };
                })
                : [];
            const pid = po.id;
            const name = typeof po.name === "string" ? po.name : `Page ${pageIdx + 1}`;
            const desc = po.description;
            const isHomeRaw = po.isHome ?? po.is_home;
            const isHome =
                typeof isHomeRaw === "boolean" ? isHomeRaw : false;
            return {
                id: typeof pid === "string" && pid.length > 0 ? pid : nanoid(),
                name,
                description: typeof desc === "string" ? desc : "",
                isHome,
                rows,
            };
        })
        : prev.pages;

    if (pages.length > 0 && !pages.some((tab) => tab.isHome)) {
        pages = pages.map((tab, index) => ({
            ...tab,
            isHome: index === 0,
        }));
    }

    const navRaw = raw.navigation;
    let navigation = prev.navigation;
    if (navRaw !== null && typeof navRaw === "object" && !Array.isArray(navRaw)) {
        const navo = navRaw as { items?: unknown };
        const layoutRaw = (navo as { layout?: unknown }).layout;
        const itemsRaw = navo.items;
        if (Array.isArray(itemsRaw)) {
            navigation = {
                layout:
                    layoutRaw === PORTAL_LAB_NAVIGATION_LAYOUTS.vertical ||
                    layoutRaw === PORTAL_LAB_NAVIGATION_LAYOUTS.horizontal
                        ? layoutRaw
                        : prev.navigation.layout,
                items: itemsRaw.map((it) => {
                    const io =
                        it && typeof it === "object" && !Array.isArray(it)
                            ? (it as Record<string, unknown>)
                            : {};
                    const nid = io.id;
                    const n = io.name;
                    const u = io.url;
                    return {
                        id: typeof nid === "string" && nid.length > 0 ? nid : nanoid(),
                        name: typeof n === "string" ? n : "",
                        url: typeof u === "string" ? u : "",
                    };
                }),
            };
        }
    }

    const bkIn = raw.brand_kit;
    let brand_kit = { ...prevBk };
    if (bkIn !== null && typeof bkIn === "object" && !Array.isArray(bkIn)) {
        const b = bkIn as Record<string, unknown>;
        const pw = b.prospect_website;
        const logo = b.logo_url;
        const backdropIn = readTrimmedPortalStringField(b, ["backdrop_url", "banner"]);
        const screenshotIn = readTrimmedPortalStringField(b, ["screenshot_url", "screenshot"]);
        const sloganIn = b.slogan;
        const titleIn = b.title;
        brand_kit = {
            ...prevBk,
            prospect_website:
                typeof pw === "string" ? pw : pw === null ? "" : prevBk.prospect_website,
            logo_url: (() => {
                if (typeof logo === "string") {
                    return logo;
                }
                if (logo === null) {
                    return "";
                }
                const legacyUploaded = b.uploaded_logo_url;
                if (
                    typeof legacyUploaded === "string" &&
                    legacyUploaded.trim().length > 0
                ) {
                    return legacyUploaded;
                }
                return prevBk.logo_url;
            })(),
            backdrop_url:
                typeof backdropIn === "string"
                    ? backdropIn
                    : backdropIn === null
                      ? ""
                      : prevBk.backdrop_url,
            screenshot_url:
                typeof screenshotIn === "string" && screenshotIn.trim()
                    ? screenshotIn
                    : screenshotIn === null
                      ? ""
                    : typeof prevBk.screenshot_url === "string" && prevBk.screenshot_url.trim()
                      ? prevBk.screenshot_url
                      : "",
            colors: parseBrandColorsFromServer(b.colors, prevBk.colors),
            font: serverFontToPortalFont(b.font, prevBk.font),
            corner_radius: parseCornerRadius(b.corner_radius, prevBk.corner_radius),
            slogan:
                typeof sloganIn === "string"
                    ? sloganIn
                    : sloganIn === null
                        ? ""
                        : prevBk.slogan,
            title:
                typeof titleIn === "string" ? titleIn : titleIn === null ? "" : prevBk.title,
        };
    }

    const chRaw = raw.chat_history;
    const chat_history: PortalLabChatMessage[] = Array.isArray(chRaw)
        ? chRaw.map((m) => {
            if (m !== null && typeof m === "object" && !Array.isArray(m)) {
                const mo = m as Record<string, unknown>;
                const content = mo.content;
                return {
                    id: nanoid(),
                    message: typeof content === "string" ? content : String(content ?? ""),
                    role: mo.role === "assistant" ? "assistant" : "user",
                };
            }
            return { id: nanoid(), message: "", role: "user" as const };
        })
        : prev.chat_history;

    const color_theme = parseColorThemeFromServer(
        raw.color_theme,
        prev.color_theme,
        brand_kit.colors
    );
    if (color_theme) {
        brand_kit = {
            ...brand_kit,
            colors: clonePortalBrandColors(color_theme.colors),
        };
    }

    const appLogoRaw = raw.app_logo ?? raw.appLogo;
    let appLogo = typeof appLogoRaw === "string" ? appLogoRaw.trim() : prev.appLogo;
    const logoFromKit = brand_kit.logo_url.trim();
    if (logoFromKit) {
        appLogo = logoFromKit;
    }

    const confirmed = raw.moodboard_confirmed;
    const moodboard_confirmed =
        typeof confirmed === "boolean" ? confirmed : prev.moodboard_confirmed;

    const buildSt = raw.portal_build_status;
    const portal_build_status =
        buildSt === null || typeof buildSt === "string"
            ? (buildSt as string | null)
            : prev.portal_build_status;

    return {
        appLogo,
        pages: pages.length > 0 ? pages : prev.pages,
        navigation,
        brand_kit,
        brandKitLogoUrls: prev.brandKitLogoUrls,
        brandKitBackdropUrls: prev.brandKitBackdropUrls,
        brandKitLogoIndex: prev.brandKitLogoIndex,
        brandKitBackdropIndex: prev.brandKitBackdropIndex,
        portal_build_status,
        chat_history,
        moodboard_confirmed,
        color_theme,
        brandColorBaseline: prev.brandColorBaseline,
        theme: parsePortalTheme(raw.theme, prev.theme),
    };
}

/**
 * Maps GET `company-portal/processing/{id}/` embedded `state` into the merge shape
 * consumed by `mergeCompanyPortalServerPayload` (uses `brand_kit`, not API `branding`).
 */
export function companyPortalEmbeddedStateToMergePayload(
    embedded: Record<string, unknown>
): Record<string, unknown> | null {
    const brandKit: Record<string, unknown> = {};

    const wu = embedded.website_url;
    if (typeof wu === "string") {
        brandKit.prospect_website = wu;
    } else if (wu === null) {
        brandKit.prospect_website = "";
    }

    const branding = embedded.branding;
    if (branding !== null && branding !== undefined && typeof branding === "object" && !Array.isArray(branding)) {
        const b = branding as Record<string, unknown>;
        if (typeof b.logo === "string") {
            brandKit.logo_url = b.logo;
        } else if (b.logo === null) {
            brandKit.logo_url = "";
        }
        if (typeof b.banner === "string") {
            brandKit.backdrop_url = b.banner;
        } else if (b.banner === null) {
            brandKit.backdrop_url = "";
        }
        if (typeof b.screenshot === "string") {
            brandKit.screenshot_url = b.screenshot;
        } else if (b.screenshot === null) {
            brandKit.screenshot_url = "";
        }
        if (b.colors !== undefined) {
            brandKit.colors = b.colors;
        }
        if (typeof b.slogan === "string") {
            brandKit.slogan = b.slogan;
        } else if (b.slogan === null) {
            brandKit.slogan = "";
        }
        if (typeof b.description === "string") {
            brandKit.title = b.description;
        } else if (b.description === null) {
            brandKit.title = "";
        }
    }

    if (Object.keys(brandKit).length === 0) {
        return null;
    }
    return { brand_kit: brandKit };
}

export interface PortalLabStep {
    label: string;
    title: string;
    description: string;
}

export interface PortalLabPageTab {
    id: string;
    name: string;
    description: string;
    isHome: boolean;
    rows: PortalLabPageRow[];
}

export interface PortalLabPageElement {
    id: string;
    type: PortalElementType;
    span: number;
    height_px?: number | null;
    config: Record<string, unknown>;
}

export interface PortalLabPageRow {
    id: string;
    elements: PortalLabPageElement[];
}

export type { PortalCornerRadius } from "@utils/constants/portalLab";

export type PortalBrandColorKey = "primary" | "secondary" | "dark" | "light" | "accent";

export type PortalBrandColors = Record<PortalBrandColorKey, string | null>;

/** Moodboard `color_theme` — active palette applied in preview (full color slots). */
export type PortalColorTheme = {
    colors: PortalBrandColors;
};

export interface PortalFontOption {
    id: string;
    name: string;
}

export type PortalLabFlowType = "new" | "existing";

export interface PortalLabNavigationItem {
    id: string;
    name: string;
    url: string;
}

export const PORTAL_LAB_NAVIGATION_LAYOUTS = {
    horizontal: "horizontal",
    vertical: "vertical",
} as const;

export type PortalLabNavigationLayout = (typeof PORTAL_LAB_NAVIGATION_LAYOUTS)[keyof typeof PORTAL_LAB_NAVIGATION_LAYOUTS];

export interface PortalLabChatMessage {
    id: string;
    message: string;
    role: "user" | "assistant";
}

export interface PortalConfig {
    /** User-uploaded logo URL (portal lab Colors section); preview prefers this over scraped `brand_kit.logo_url`. */
    appLogo: string;
    pages: PortalLabPageTab[];
    navigation: {
        layout: PortalLabNavigationLayout;
        items: PortalLabNavigationItem[];
    };
    brand_kit: {
        prospect_website: string;
        logo_url: string;
        backdrop_url: string;
        screenshot_url: string;
        colors: PortalBrandColors;
        font: string;
        corner_radius: PortalCornerRadius;
        slogan: string;
        title: string;
    };
    brandKitLogoUrls: string[];
    brandKitBackdropUrls: string[];
    brandKitLogoIndex: number;
    brandKitBackdropIndex: number;
    portal_build_status: string | null;
    chat_history: PortalLabChatMessage[];
    moodboard_confirmed: boolean;
    color_theme: PortalColorTheme | null;
    /** Original brand-kit colors for preview shuffle palette 1 (unchanged by shuffles). */
    brandColorBaseline: PortalBrandColors | null;
    /** Style tokens (density/card style/chart palette/...) — disjoint from brand_kit. */
    theme: PortalThemeTokens;
}

export interface PortalLabSelectedLiveboard {
    id: string;
    name: string;
}

export type PortalLabConnectionType = "thoughtspot" | "databricks" | null;

interface PortalLabState {
    completedStep: number;
    currentStep: number;
    steps: PortalLabStep[];
    definePortalTabIndex: number;
    flowType: PortalLabFlowType | null;
    processingId: string | null;
    stage: string | null;
    selectedPageId: string;
    portalConfig: PortalConfig;
    integrationPipelines: Pipeline[];
    selectedPipelineId: number | null;
    selectedConnectionId: number | null;
    connection_type: PortalLabConnectionType;
    connection_name: string | null;
    deploymentUrl: string | null;
    selectedLiveboard: PortalLabSelectedLiveboard | null;
    liveboardOptions: { id: string; name: string }[];
    /** AI-generated HTML from `GET .../processing/:id/template/` after step-2 save. */
    generatedTemplate: PortalLabCompanyPortalProcessingTemplateResponse | null;
}

const initialSteps: PortalLabStep[] = [
    {
        label: "Step 1",
        title: "Define",
        description: "Configure pages, navigation, and branding for your customer portal.",
    },
    {
        label: "Step 2",
        title: "Preview",
        description: "Review your generated portal before deploying it.",
    },
    {
        label: "Step 3",
        title: "Deploy",
        description: "Deploy your portal to open the live customer experience.",
    },
];

const initialPageTabs: PortalLabPageTab[] = [
    {
        id: "61c7b723cae144a9a9a4417c3937e247",
        name: "Executive Overview",
        description: "Executive Overview",
        isHome: true,
        rows: [
            {
                id: "2208c74f57e942aa8f855f0819c2dfc0",
                elements: [
                    {
                        id: "8b86a29f7527470e992a18939b7ba508",
                        type: "banner_hero",
                        span: 12,
                        config: {},
                    },
                ],
            },
            {
                id: "3714a1dc415d43daa02bdc63537fd507",
                elements: [
                    {
                        id: "c50c799f323b4642bda21a9517099923",
                        type: "kpi_block",
                        span: 3,
                        config: {},
                    },
                    {
                        id: "2b5ca7285e6d4d1e87afa09d5df558c5",
                        type: "kpi_block",
                        span: 3,
                        config: {},
                    },
                    {
                        id: "58443cf2c2e64322a9234bf5ea69f069",
                        type: "kpi_block",
                        span: 3,
                        config: {},
                    },
                    {
                        id: "dcd9af5c4b8a4adaac8f3bae2b78b9a1",
                        type: "kpi_block",
                        span: 3,
                        config: {},
                    },
                ],
            },
            {
                id: "8b997cc470d34f449707f30bff2b95bc",
                elements: [
                    {
                        id: "11a6fbdb697040f891a7b81d8704a960",
                        type: "liveboard_area",
                        span: 6,
                        config: {},
                    },
                    {
                        id: "a92db40896f54e7daa1e71d3e3af1c58",
                        type: "ai_chat_widget",
                        span: 6,
                        config: {},
                    },
                ],
            },
        ],
    },
    /*
    {
        id: "3714a1dc415d43daa02bdc63537fd507",
        name: "Analytics",
        description: "Analytics Hub",
        isHome: false,
        rows: [
            {
                id: "2d5cdd8f7c87407986e5f7b6d8a03fac",
                elements: [
                    {
                        id: "b09b6e9ce58b46878d549d119492a1e4",
                        type: "banner_hero",
                        span: 12,
                        config: {},
                    },
                ],
            },
            {
                id: "e80aa15480c04c5998f7446e5f137b52",
                elements: [
                    {
                        id: "d671db0e9c214cc0b938fc4560a9ed24",
                        type: "kpi_block",
                        span: 4,
                        config: {},
                    },
                    {
                        id: "bd6f9ee0a6374e8db3a3807184ace1ed",
                        type: "kpi_block",
                        span: 4,
                        config: {},
                    },
                    {
                        id: "81b33b4e43f348d5aac2f05767cf0bda",
                        type: "kpi_block",
                        span: 4,
                        config: {},
                    },
                ],
            },
            {
                id: "f44e9199a0b54bbbb323db830715ef0d",
                elements: [
                    {
                        id: "c2affb35c0164e9f8516c92277e238cc",
                        type: "liveboard_area",
                        span: 12,
                        config: {},
                    },
                ],
            },
            {
                id: "8eac5ea2f6174f9da249ce938cf8c4ac",
                elements: [
                    {
                        id: "e85c211a7ee94369a1ebc41b5080e571",
                        type: "ai_chat_widget",
                        span: 12,
                        config: {},
                    },
                ],
            },
        ],
    },
    */
];

const clonePageTabs = (tabs: PortalLabPageTab[]): PortalLabPageTab[] =>
    tabs.map((tab) => ({
        ...tab,
        rows: tab.rows.map((row) => ({
            ...row,
            elements: row.elements.map((element) => ({
                ...element,
                config: { ...element.config },
            })),
        })),
    }));

const createPortalElement = (
    blockId: PortalElementBlockId,
    span = 12
): PortalLabPageElement => ({
    id: nanoid(),
    type: getPortalElementTypeFromBlockId(blockId),
    span,
    config: {},
});

const createPortalElements = (
    blockId: PortalElementBlockId,
    span = 12
): PortalLabPageElement[] => {
    if (blockId === "kpi-block-group") {
        return Array.from({ length: 4 }, () => createPortalElement("kpi-block", span));
    }
    return [createPortalElement(blockId, span)];
};

const normalizeRowSpans = (row: PortalLabPageRow): void => {
    if (row.elements.length === 0) {
        return;
    }
    const span = Math.max(1, Math.floor(12 / row.elements.length));
    row.elements.forEach((element) => {
        element.span = span;
    });
};

const createRowsFromPreviewRows = (
    previewRows: PortalElementBlockId[][]
): PortalLabPageRow[] =>
    previewRows.map((row) => {
        const portalRow: PortalLabPageRow = {
            id: nanoid(),
            elements: row.map((blockId) => createPortalElement(blockId)),
        };
        normalizeRowSpans(portalRow);
        return portalRow;
    });

export const getPortalLabElementBlockId = (
    element: Pick<PortalLabPageElement, "type">
): PortalElementBlockId | "navigation-menu" =>
    getPortalBlockIdFromElementType(element.type);

type BrandKitExtracted = {
    logo_url: string;
    backdrop_url: string;
    colors: PortalBrandColors;
    slogan: string;
    title: string;
};

const emptyPortalBrandColors = (): PortalBrandColors => ({
    primary: null,
    secondary: null,
    dark: null,
    light: null,
    accent: null,
});

function clonePortalBrandColors(colors: PortalBrandColors): PortalBrandColors {
    return { ...colors };
}

/** Display / shuffle order for portal brand colors (Colors + Preview sidebars). */
export const PORTAL_BRAND_COLOR_KEYS: PortalBrandColorKey[] = [
    "primary",
    "secondary",
    "dark",
    "light",
    "accent",
];

export function buildPortalColorTheme(colors: PortalBrandColors): PortalColorTheme {
    return { colors: clonePortalBrandColors(colors) };
}

function normalizeBrandColorHex(value: string | null): string {
    if (typeof value === "string" && value.trim().length > 0) {
        return value.trim().toLowerCase();
    }

    return "#e5e7eb";
}

export function portalBrandColorsMatch(
    a: PortalBrandColors,
    b: PortalBrandColors
): boolean {
    return PORTAL_BRAND_COLOR_KEYS.every(
        (key) => normalizeBrandColorHex(a[key]) === normalizeBrandColorHex(b[key])
    );
}

function parseColorThemeFromServer(
    raw: unknown,
    fallback: PortalColorTheme | null,
    brandColors: PortalBrandColors
): PortalColorTheme | null {
    if (raw === null) {
        return null;
    }

    if (typeof raw === "string") {
        return null;
    }

    if (typeof raw === "object" && !Array.isArray(raw)) {
        const themeObj = raw as Record<string, unknown>;
        if (themeObj.colors !== undefined) {
            return {
                colors: parseBrandColorsFromServer(themeObj.colors, brandColors),
            };
        }
    }

    return fallback;
}

const normalizePortalBrandColorKey = (name?: string): PortalBrandColorKey | null => {
    const normalized = name?.trim().toLowerCase().replace(/[\s_-]+/g, "") ?? "";
    if (normalized === "primary") return "primary";
    if (normalized === "secondary") return "secondary";
    if (normalized === "dark") return "dark";
    if (normalized === "light") return "light";
    if (normalized === "accent") return "accent";
    return null;
};

/** Prefer SVG/PNG assets — usually better transparency than JPEG scrapes. */
function logoAssetPreferenceScore(url: string): number {
    const lower = url.toLowerCase();
    if (lower.includes(".svg") || lower.includes("image/svg")) return 0;
    if (lower.includes(".png") || lower.includes("image/png")) return 1;
    if (lower.includes(".webp") || lower.includes("image/webp")) return 2;
    return 3;
}

function preferLogoUrls(urls: string[]): string[] {
    return [...urls].sort(
        (a, b) => logoAssetPreferenceScore(a) - logoAssetPreferenceScore(b)
    );
}

function extractBrandKitFromRaw(raw: unknown): BrandKitExtracted {
    const empty: BrandKitExtracted = {
        logo_url: "",
        backdrop_url: "",
        slogan: "",
        title: "",
        colors: emptyPortalBrandColors(),
    };

    if (raw == null || typeof raw !== "object") {
        return empty;
    }

    const companyPortalPalette = (raw as { colors?: unknown }).colors;
    if (
        companyPortalPalette !== null &&
        typeof companyPortalPalette === "object" &&
        !Array.isArray(companyPortalPalette)
    ) {
        const palette = companyPortalPalette as Record<string, unknown>;
        const hasNamedHex = PORTAL_BRAND_COLOR_KEYS.some((key) => {
            const val = palette[key];
            return typeof val === "string" && val.trim().length > 0;
        });
        if (hasNamedHex) {
            const colors = emptyPortalBrandColors();
            for (const key of PORTAL_BRAND_COLOR_KEYS) {
                const val = palette[key];
                if (typeof val === "string" && val.trim()) {
                    colors[key] = val.trim();
                }
            }
            return { ...empty, colors };
        }
    }

    const brand = (raw as { brand?: Record<string, unknown> }).brand;
    if (!brand || typeof brand !== "object") {
        return empty;
    }

    const title = typeof brand.title === "string" ? brand.title.trim() : "";
    const slogan = typeof brand.slogan === "string" ? brand.slogan.trim() : "";

    const colorsRaw = Array.isArray(brand.colors) ? brand.colors : [];
    const colors = emptyPortalBrandColors();
    let fallbackColorIndex = 0;
    for (const c of colorsRaw) {
        if (!c || typeof c !== "object") continue;
        const hex =
            typeof (c as { hex?: unknown }).hex === "string"
                ? (c as { hex: string }).hex.trim()
                : "";
        if (!hex) continue;
        const nameRaw = (c as { name?: unknown }).name;
        const name =
            typeof nameRaw === "string" ? nameRaw.trim() : undefined;
        const namedKey = normalizePortalBrandColorKey(name);
        if (namedKey) {
            colors[namedKey] = hex;
            continue;
        }
        while (
            fallbackColorIndex < PORTAL_BRAND_COLOR_KEYS.length &&
            colors[PORTAL_BRAND_COLOR_KEYS[fallbackColorIndex]] !== null
        ) {
            fallbackColorIndex += 1;
        }
        const fallbackKey = PORTAL_BRAND_COLOR_KEYS[fallbackColorIndex];
        if (fallbackKey) {
            colors[fallbackKey] = hex;
            fallbackColorIndex += 1;
        }
    }

    const logosRaw = Array.isArray(brand.logos) ? brand.logos : [];
    const logoUrls = preferLogoUrls(
        logosRaw
            .map((logo) => {
                if (!logo || typeof logo !== "object") return "";
                const url = typeof (logo as { url?: unknown }).url === "string"
                    ? (logo as { url: string }).url.trim()
                    : "";
                return url;
            })
            .filter((url) => url.length > 0)
    );

    const backdropsRaw = Array.isArray(brand.backdrops) ? brand.backdrops : [];
    const backdropUrls = backdropsRaw
        .map((bd) => {
            if (!bd || typeof bd !== "object") return "";
            const url = typeof (bd as { url?: unknown }).url === "string"
                ? (bd as { url: string }).url.trim()
                : "";
            return url;
        })
        .filter((url) => url.length > 0);

    return {
        logo_url: logoUrls[0] ?? "",
        backdrop_url: backdropUrls[0] ?? "",
        slogan,
        title,
        colors,
    };
}

function extractBrandAssetUrls(raw: unknown): {
    logoUrls: string[];
    backdropUrls: string[];
} {
    if (raw == null || typeof raw !== "object") {
        return { logoUrls: [], backdropUrls: [] };
    }
    const brand = (raw as { brand?: Record<string, unknown> }).brand;
    if (!brand || typeof brand !== "object") {
        return { logoUrls: [], backdropUrls: [] };
    }
    const logosRaw = Array.isArray(brand.logos) ? brand.logos : [];
    const logoUrls = preferLogoUrls(
        logosRaw
            .map((logo) => {
                if (!logo || typeof logo !== "object") return "";
                const url = typeof (logo as { url?: unknown }).url === "string"
                    ? (logo as { url: string }).url.trim()
                    : "";
                return url;
            })
            .filter((url) => url.length > 0)
    );

    const backdropsRaw = Array.isArray(brand.backdrops) ? brand.backdrops : [];
    const backdropUrls = backdropsRaw
        .map((bd) => {
            if (!bd || typeof bd !== "object") return "";
            const url = typeof (bd as { url?: unknown }).url === "string"
                ? (bd as { url: string }).url.trim()
                : "";
            return url;
        })
        .filter((url) => url.length > 0);

    return { logoUrls, backdropUrls };
}

const nextRandomIndex = (length: number, current: number): number => {
    if (length <= 0) return 0;
    if (length === 1) return 0;
    let next = current;
    while (next === current) {
        next = Math.floor(Math.random() * length);
    }
    return next;
};

const initialPortalConfig = (): PortalConfig => ({
    appLogo: "",
    pages: clonePageTabs(initialPageTabs),
    navigation: {
        layout: PORTAL_LAB_NAVIGATION_LAYOUTS.horizontal,
        items: []
    },
    brand_kit: {
        prospect_website: "",
        logo_url: "",
        backdrop_url: "",
        screenshot_url: "",
        colors: emptyPortalBrandColors(),
        font: "System Font",
        corner_radius: "m",
        slogan: "",
        title: "",
    },
    brandKitLogoUrls: [],
    brandKitBackdropUrls: [],
    brandKitLogoIndex: 0,
    brandKitBackdropIndex: 0,
    portal_build_status: null,
    chat_history: [],
    moodboard_confirmed: false,
    color_theme: null,
    brandColorBaseline: null,
    theme: { ...DEFAULT_PORTAL_THEME },
});


export function portalConfigToCompanyPortalPutBody(config: PortalConfig): Record<string, unknown> {
    const { pages, navigation, brand_kit, color_theme, chat_history, appLogo, brandColorBaseline } =
        config;
    const appLogoTrimmed = appLogo.trim() || null;
    const defaultPalette = brandColorBaseline ?? brand_kit.colors;
    const effectiveColorTheme =
        color_theme ?? buildPortalColorTheme(defaultPalette);
    return {
        appLogo: appLogoTrimmed,
        pages: pages.map((tab) => ({
            id: tab.id,
            name: tab.name,
            description: tab.description || null,
            isHome: tab.isHome,
            rows: tab.rows.map((row) => ({
                id: row.id,
                elements: row.elements.map((el) => ({
                    id: el.id,
                    type: el.type,
                    span: el.span,
                    height_px: el.height_px ?? null,
                    config: el.config,
                })),
            })),
        })),
        navigation: {
            layout: navigation.layout,
            items: navigation.items.map((item) => ({
                id: item.id,
                name: item.name,
                url: item.url,
            })),
        },
        brand_kit: {
            prospect_website: brand_kit.prospect_website || null,
            logo_url: brand_kit.logo_url || null,
            screenshot_url: brand_kit.screenshot_url || null,
            colors: { ...brand_kit.colors },
            font: brand_kit.font,
            font_url: resolvePortalFontUrl(brand_kit.font),
            corner_radius: brand_kit.corner_radius,
            slogan: brand_kit.slogan.trim() || null,
            title: brand_kit.title.trim() || null,
        },
        color_theme: { colors: { ...effectiveColorTheme.colors } },
        chat_history: chat_history.map((m) => ({ role: m.role, content: m.message })),
    };
}

/** Shape the live preview renderer consumes (MoodboardJson). Colours fall back to a neutral grey. */
export function portalConfigToMoodboardJson(config: PortalConfig): MoodboardJson {
    const { pages, navigation, brand_kit, color_theme } = config;
    const color = (value: string | null): string => value || "#e5e7eb";
    const colors = {
        primary: color(brand_kit.colors.primary),
        secondary: color(brand_kit.colors.secondary),
        dark: color(brand_kit.colors.dark),
        light: color(brand_kit.colors.light),
        accent: color(brand_kit.colors.accent),
    };
    return {
        pages: pages.map((tab) => ({
            id: tab.id,
            name: tab.name,
            description: tab.description,
            isHome: tab.isHome,
            rows: tab.rows.map((row) => ({
                id: row.id,
                elements: row.elements.map((el) => ({
                    id: el.id,
                    type: el.type,
                    span: el.span,
                    height_px: el.height_px ?? null,
                    config: el.config,
                })),
            })),
        })),
        navigation: { layout: navigation.layout, items: navigation.items },
        brand_kit: {
            prospect_website: brand_kit.prospect_website,
            logo_url: brand_kit.logo_url,
            colors,
            font: brand_kit.font,
            font_url: resolvePortalFontUrl(brand_kit.font),
            corner_radius: brand_kit.corner_radius,
            slogan: brand_kit.slogan,
            title: brand_kit.title,
            backdrop_url: brand_kit.backdrop_url,
        },
        color_theme: color_theme ? { colors } : null,
        chat_history: [],
    };
}

/** Wrapped step-2 body: routes to the new synchronous, AI-free backend path. */
export function portalConfigToStep2Body(config: PortalConfig): Record<string, unknown> {
    return {
        moodboard: portalConfigToCompanyPortalPutBody(config),
        theme: { ...config.theme },
    };
}

const initialState: PortalLabState = {
    completedStep: 0,
    currentStep: 1,
    steps: initialSteps,
    definePortalTabIndex: 0,
    flowType: null,
    processingId: null,
    stage: null,
    selectedPageId: initialPageTabs[0]?.id ?? "",
    portalConfig: initialPortalConfig(),
    integrationPipelines: [],
    selectedPipelineId: null,
    selectedConnectionId: null,
    connection_type: null,
    connection_name: null,
    deploymentUrl: null,
    selectedLiveboard: null,
    liveboardOptions: [],
    generatedTemplate: null,
};

const portalLabSlice = createSlice({
    name: "portalLab",
    initialState,
    reducers: {
        portalLabNextStep(state) {
            if (state.completedStep < state.currentStep) {
                return;
            }
            if (state.currentStep < state.steps.length) {
                state.currentStep += 1;
            }
        },
        portalLabSetCompletedStep(state, action: PayloadAction<number>) {
            const cap = Math.max(state.steps.length, 0);
            state.completedStep = Math.max(0, Math.min(action.payload, cap));
        },
        portalLabPrevStep(state) {
            if (state.currentStep > 0) {
                state.currentStep -= 1;
            }
        },
        portalLabSetCurrentStep(state, action: PayloadAction<number>) {
            const next = action.payload;
            if (next >= 0 && next <= state.steps.length) {
                state.currentStep = next;
            }
        },
        portalLabSetDefinePortalTabIndex(state, action: PayloadAction<number>) {
            state.definePortalTabIndex = action.payload;
        },
        portalLabStartFlow(
            state,
            action: PayloadAction<{
                flowType: PortalLabFlowType;
                prospect_website: string;
                processingId: string | null;
                pipelineId?: number | null;
                connection_type?: PortalLabConnectionType;
            }>
        ) {
            state.flowType = action.payload.flowType;
            state.portalConfig.brand_kit.prospect_website = action.payload.prospect_website;
            state.processingId = action.payload.processingId;
            state.deploymentUrl = null;
            if (action.payload.pipelineId !== undefined) {
                state.selectedPipelineId = action.payload.pipelineId;
                state.selectedConnectionId = null;
                state.connection_type =
                    action.payload.connection_type === "databricks" ||
                    action.payload.connection_type === "thoughtspot"
                        ? action.payload.connection_type
                        : null;
                state.connection_name = null;
                state.selectedLiveboard = null;
                state.liveboardOptions = [];
            }
        },
        portalLabSetIntegrationPipelines(state, action: PayloadAction<Pipeline[]>) {
            state.integrationPipelines = action.payload;
            if (
                state.selectedConnectionId != null &&
                state.selectedPipelineId === null
            ) {
                const cid = state.selectedConnectionId;
                const pipeline = state.integrationPipelines.find((p) =>
                    pipelineContainsConnectionId(p, cid)
                );
                state.selectedPipelineId = pipeline?.id ?? null;
            }
        },
        portalLabSetStage(state, action: PayloadAction<string | null>) {
            const previousStage = state.stage;
            state.stage = action.payload;
            // Keep wizard page in sync when stage advances (e.g. Deployment Processing).
            applyPortalLabWizardStepFromStage(state, action.payload, previousStage);
        },
        portalLabSetGeneratedTemplate(
            state,
            action: PayloadAction<PortalLabCompanyPortalProcessingTemplateResponse>
        ) {
            state.generatedTemplate = action.payload;
        },
        portalLabClearGeneratedTemplate(state) {
            state.generatedTemplate = null;
        },
        /** Clears cached HTML and marks step 2 in progress after moodboard re-save. */
        portalLabBeginTemplateRegeneration(state) {
            state.generatedTemplate = null;
            state.stage = COMPANY_PORTAL_STAGE_STEP2_PROCESSING;
        },
        /** Optimistically append a user chat message before the copilot responds. */
        portalLabAppendChatMessage(state, action: PayloadAction<string>) {
            state.portalConfig.chat_history.push({ id: nanoid(), message: action.payload, role: "user" });
        },
        /** Merge the design copilot's structured result (moodboard + theme + chat) into config. */
        portalLabApplyChatResult(
            state,
            action: PayloadAction<{ moodboard?: Record<string, unknown>; theme?: Record<string, unknown> }>
        ) {
            const { moodboard, theme } = action.payload;
            if (!moodboard || typeof moodboard !== "object") {
                return;
            }
            const raw = theme && typeof theme === "object" ? { ...moodboard, theme } : moodboard;
            state.portalConfig = mergeCompanyPortalServerPayload(raw as Record<string, unknown>, state.portalConfig);
        },
        portalLabSetThemeMode(state, action: PayloadAction<PortalThemeTokens["mode"]>) {
            state.portalConfig.theme.mode = action.payload;
        },
        portalLabSetThemeDensity(state, action: PayloadAction<PortalThemeTokens["density"]>) {
            state.portalConfig.theme.density = action.payload;
        },
        portalLabSetCardStyle(state, action: PayloadAction<PortalThemeTokens["card_style"]>) {
            state.portalConfig.theme.card_style = action.payload;
        },
        portalLabSetElementHeight(
            state,
            action: PayloadAction<{ elementId: string; height_px: number | null }>
        ) {
            for (const page of state.portalConfig.pages) {
                for (const row of page.rows) {
                    for (const element of row.elements) {
                        if (element.id === action.payload.elementId) {
                            element.height_px = action.payload.height_px;
                        }
                    }
                }
            }
        },
        portalLabSyncStageFromCompanyPortalEntity(
            state,
            action: PayloadAction<Record<string, unknown>>
        ) {
            const embedded = action.payload.state;
            if (
                embedded === null ||
                typeof embedded !== "object" ||
                Array.isArray(embedded)
            ) {
                state.stage = null;
                state.deploymentUrl = null;
                return;
            }
            const em = embedded as Record<string, unknown>;
            const s = em.stage;
            const previousStage = state.stage;
            state.stage = typeof s === "string" ? s : null;

            const deployment = parseDeploymentUrlFromCompanyPortalEmbedded(em);
            if (deployment !== "omit") {
                state.deploymentUrl = deployment;
            }

            applyPortalLabWizardStepFromStage(state, state.stage, previousStage);
        },
        /** Sets stack + connection from `GET company-portal/processing/:id/` (`state.pipeline_id`, `state.connection_details`). */
        portalLabSetIntegrationSelectionFromCompanyPortalEntity(
            state,
            action: PayloadAction<Record<string, unknown>>
        ) {
            const embedded = action.payload.state;
            if (
                embedded === null ||
                typeof embedded !== "object" ||
                Array.isArray(embedded)
            ) {
                return;
            }
            const em = embedded as Record<string, unknown>;
            if ("pipeline_id" in em) {
                const pid = parsePositiveIntFromCompanyPortalField(em["pipeline_id"]);
                if (pid !== null) {
                    state.selectedPipelineId = pid;
                }
            }
            if (typeof em.connection_type === "string") {
                const rootConnectionType = em.connection_type.trim().toLowerCase();
                state.connection_type =
                    rootConnectionType === "databricks"
                        ? "databricks"
                        : rootConnectionType === "thoughtspot"
                            ? "thoughtspot"
                            : state.connection_type;
            }
            if (typeof em.connection_name === "string" && em.connection_name.trim()) {
                state.connection_name = em.connection_name.trim();
            }
            const cd = em["connection_details"];
            if (cd === undefined) {
                return;
            }
            const cid = parseConnectionIdFromCompanyPortalState(cd);
            if (cid === null) {
                return;
            }
            state.selectedConnectionId = cid;
            const connectionType =
                parseConnectionTextFromCompanyPortalState(cd, [
                    "connection_type",
                    "provider",
                    "platform",
                    "integration_type",
                ])?.toLowerCase() ??
                (typeof em.connection_type === "string" ? em.connection_type.trim().toLowerCase() : null);
            state.connection_type =
                connectionType === "databricks"
                    ? "databricks"
                    : connectionType === "thoughtspot"
                        ? "thoughtspot"
                        : state.connection_type;
            state.connection_name = parseConnectionTextFromCompanyPortalState(cd, [
                "connection_name",
                "name",
            ]) ?? (typeof em.connection_name === "string" && em.connection_name.trim() ? em.connection_name.trim() : state.connection_name);
            if (state.selectedPipelineId === null) {
                const pipeline = state.integrationPipelines.find((p) =>
                    pipelineContainsConnectionId(p, cid)
                );
                state.selectedPipelineId = pipeline?.id ?? null;
            }
        },
        portalLabHydrateFromCompanyPortalState(
            state,
            action: PayloadAction<Record<string, unknown>>
        ) {
            state.portalConfig = mergeCompanyPortalServerPayload(
                action.payload,
                state.portalConfig
            );
            const pages = state.portalConfig.pages;
            if (
                pages.length > 0 &&
                !pages.some((p) => p.id === state.selectedPageId)
            ) {
                state.selectedPageId = pages[0].id;
            }
        },
        portalLabSetSelectedPageId(state, action: PayloadAction<string>) {
            state.selectedPageId = action.payload;
        },
        portalLabSetBrandKitResponse(state, action: PayloadAction<unknown | null>) {
            const { logo_url, backdrop_url, colors, slogan, title } =
                extractBrandKitFromRaw(action.payload);
            const { logoUrls, backdropUrls } = extractBrandAssetUrls(action.payload);
            const cfg = state.portalConfig;
            cfg.brandKitLogoUrls = logoUrls;
            cfg.brandKitBackdropUrls = backdropUrls;
            cfg.brandKitLogoIndex = 0;
            cfg.brandKitBackdropIndex = 0;
            cfg.brand_kit.logo_url = logoUrls[0] ?? logo_url ?? cfg.brand_kit.logo_url;
            cfg.brand_kit.backdrop_url =
                backdropUrls[0] ?? backdrop_url ?? cfg.brand_kit.backdrop_url;
            cfg.brand_kit.colors = colors;
            cfg.brandColorBaseline = clonePortalBrandColors(colors);
            cfg.brand_kit.slogan = slogan;
            cfg.brand_kit.title = title;
        },
        portalLabEnsureBrandColorBaseline(state) {
            const cfg = state.portalConfig;
            if (!cfg.brandColorBaseline) {
                cfg.brandColorBaseline = clonePortalBrandColors(cfg.brand_kit.colors);
            }
        },
        portalLabRestoreBrandColorBaseline(state) {
            const cfg = state.portalConfig;
            if (!cfg.brandColorBaseline) {
                cfg.brandColorBaseline = clonePortalBrandColors(cfg.brand_kit.colors);
            }
            const baseline = clonePortalBrandColors(cfg.brandColorBaseline);
            cfg.brand_kit.colors = baseline;
            cfg.color_theme = buildPortalColorTheme(baseline);
        },
        portalLabInitPreviewColorTheme(state) {
            const cfg = state.portalConfig;
            if (!cfg.brandColorBaseline) {
                cfg.brandColorBaseline = clonePortalBrandColors(cfg.brand_kit.colors);
            }
            if (!cfg.color_theme) {
                const baseline = clonePortalBrandColors(cfg.brandColorBaseline);
                cfg.brand_kit.colors = baseline;
                cfg.color_theme = buildPortalColorTheme(baseline);
            }
        },
        portalLabShufflePreviewLogo(state) {
            const cfg = state.portalConfig;
            const next = nextRandomIndex(
                cfg.brandKitLogoUrls.length,
                cfg.brandKitLogoIndex
            );
            cfg.brandKitLogoIndex = next;
            if (cfg.brandKitLogoUrls.length > 0) {
                cfg.brand_kit.logo_url =
                    cfg.brandKitLogoUrls[next] ?? cfg.brand_kit.logo_url;
            }
        },
        portalLabShufflePreviewLogoAndBackdrop(state) {
            const cfg = state.portalConfig;
            const nextLogo = nextRandomIndex(
                cfg.brandKitLogoUrls.length,
                cfg.brandKitLogoIndex
            );
            cfg.brandKitLogoIndex = nextLogo;
            if (cfg.brandKitLogoUrls.length > 0) {
                cfg.brand_kit.logo_url =
                    cfg.brandKitLogoUrls[nextLogo] ?? cfg.brand_kit.logo_url;
            }

            const nextBackdrop = nextRandomIndex(
                cfg.brandKitBackdropUrls.length,
                cfg.brandKitBackdropIndex
            );
            cfg.brandKitBackdropIndex = nextBackdrop;
            if (cfg.brandKitBackdropUrls.length > 0) {
                cfg.brand_kit.backdrop_url =
                    cfg.brandKitBackdropUrls[nextBackdrop] ?? cfg.brand_kit.backdrop_url;
            }
        },
        portalLabSetLogoUrl(state, action: PayloadAction<string | null>) {
            state.portalConfig.brand_kit.logo_url = action.payload ?? "";
        },
        portalLabSetAppLogo(state, action: PayloadAction<string | null>) {
            const trimmed = action.payload?.trim() ?? "";
            state.portalConfig.appLogo = trimmed;
            state.portalConfig.brand_kit.logo_url = trimmed;
        },
        portalLabSetBackdropUrl(state, action: PayloadAction<string | null>) {
            const next = action.payload ?? "";
            const cfg = state.portalConfig;
            cfg.brand_kit.backdrop_url = next;
            if (!next) {
                return;
            }
            const existsAt = cfg.brandKitBackdropUrls.findIndex((url) => url === next);
            if (existsAt >= 0) {
                cfg.brandKitBackdropIndex = existsAt;
                return;
            }
            cfg.brandKitBackdropUrls = [next, ...cfg.brandKitBackdropUrls];
            cfg.brandKitBackdropIndex = 0;
        },
        portalLabSetCornerRadius(state, action: PayloadAction<PortalCornerRadius>) {
            state.portalConfig.brand_kit.corner_radius = action.payload;
        },
        portalLabSetPortalFont(state, action: PayloadAction<string>) {
            state.portalConfig.brand_kit.font = action.payload;
        },
        portalLabApplyColorTheme(
            state,
            action: PayloadAction<{
                colors: unknown;
                /** When true, also updates the stored brand baseline (Colors step edits). */
                syncBaseline?: boolean;
            }>
        ) {
            const cfg = state.portalConfig;
            const nextColors = parseBrandColorsFromServer(
                action.payload.colors,
                cfg.brand_kit.colors
            );
            cfg.brand_kit.colors = nextColors;
            cfg.color_theme = buildPortalColorTheme(nextColors);
            if (action.payload.syncBaseline) {
                cfg.brandColorBaseline = clonePortalBrandColors(nextColors);
            }
        },
        portalLabAddPageTab(state, action: PayloadAction<PortalLabPageTab>) {
            const page = {
                ...action.payload,
                isHome: action.payload.isHome ?? false,
            };
            state.portalConfig.pages.push(page);
            state.selectedPageId = page.id;
        },
        portalLabRemovePageTab(state, action: PayloadAction<string>) {
            const removedId = action.payload;
            const tabs = state.portalConfig.pages;
            const removedPage = tabs.find((tab) => tab.id === removedId);
            state.portalConfig.pages = tabs.filter((tab) => tab.id !== removedId);
            if (state.selectedPageId === removedId) {
                state.selectedPageId = state.portalConfig.pages[0]?.id ?? "";
            }
            if (removedPage?.isHome && state.portalConfig.pages.length > 0) {
                state.portalConfig.pages[0].isHome = true;
            }
        },
        portalLabUpdatePageTabName(
            state,
            action: PayloadAction<{ id: string; name: string }>
        ) {
            const page = state.portalConfig.pages.find(
                (tab) => tab.id === action.payload.id
            );
            if (!page) {
                return;
            }
            page.name = action.payload.name;
        },
        portalLabMovePageTab(
            state,
            action: PayloadAction<{ fromIndex: number; toIndex: number }>
        ) {
            const { fromIndex, toIndex } = action.payload;
            const tabs = state.portalConfig.pages;
            if (
                fromIndex < 0 ||
                toIndex < 0 ||
                fromIndex >= tabs.length ||
                toIndex >= tabs.length ||
                fromIndex === toIndex
            ) {
                return;
            }
            const [moved] = tabs.splice(fromIndex, 1);
            tabs.splice(toIndex, 0, moved);
        },
        portalLabSetNavigationLayout(state, action: PayloadAction<PortalLabNavigationLayout>) {
            state.portalConfig.navigation.layout = action.payload;
        },
        portalLabSetHomePage(state, action: PayloadAction<string>) {
            const tabs = state.portalConfig.pages;
            const fromIndex = tabs.findIndex((tab) => tab.id === action.payload);
            if (fromIndex < 0) {
                return;
            }
            tabs.forEach((tab) => {
                tab.isHome = false;
            });
            tabs[fromIndex].isHome = true;
            state.selectedPageId = tabs[fromIndex].id;
        },
        portalLabApplyTemplateToSelectedPage(
            state,
            action: PayloadAction<PortalPageTemplate>
        ) {
            const page = state.portalConfig.pages.find(
                (tab) => tab.id === state.selectedPageId
            );
            if (!page) {
                return;
            }
            page.name = action.payload.title;
            page.description = action.payload.subtitle;
            page.rows = createRowsFromPreviewRows(action.payload.previewRows);
        },
        portalLabAddPreviewBlock(
            state,
            action: PayloadAction<{
                blockId: PortalElementBlockId;
                targetRowIndex?: number;
                /** Insert a new full-width row at this index (0 = top). */
                insertRowIndex?: number;
            }>
        ) {
            const { blockId, targetRowIndex, insertRowIndex } = action.payload;
            if (!VALID_PREVIEW_BLOCK_IDS.has(blockId)) {
                return;
            }
            const page = state.portalConfig.pages.find(
                (tab) => tab.id === state.selectedPageId
            );
            if (!page) {
                return;
            }
            if (insertRowIndex !== undefined) {
                const at = Math.max(
                    0,
                    Math.min(Math.floor(insertRowIndex), page.rows.length)
                );
                const newRow: PortalLabPageRow = {
                    id: nanoid(),
                    elements: createPortalElements(blockId, 12),
                };
                normalizeRowSpans(newRow);
                page.rows.splice(at, 0, {
                    ...newRow,
                });
                return;
            }
            if (
                targetRowIndex !== undefined &&
                targetRowIndex >= 0 &&
                targetRowIndex < page.rows.length
            ) {
                const targetRow = page.rows[targetRowIndex];
                targetRow.elements.push(...createPortalElements(blockId));
                normalizeRowSpans(targetRow);
                return;
            }
            const newRow: PortalLabPageRow = {
                id: nanoid(),
                elements: createPortalElements(blockId, 12),
            };
            normalizeRowSpans(newRow);
            page.rows.push(newRow);
        },
        portalLabRemovePreviewCell(
            state,
            action: PayloadAction<{ rowIndex: number; cellIndex: number }>
        ) {
            const { rowIndex, cellIndex } = action.payload;
            const page = state.portalConfig.pages.find(
                (tab) => tab.id === state.selectedPageId
            );
            if (!page) {
                return;
            }
            if (rowIndex < 0 || rowIndex >= page.rows.length) {
                return;
            }
            const row = page.rows[rowIndex];
            if (!row || cellIndex < 0 || cellIndex >= row.elements.length) {
                return;
            }
            row.elements.splice(cellIndex, 1);
            if (row.elements.length === 0) {
                page.rows.splice(rowIndex, 1);
                return;
            }
            normalizeRowSpans(row);
        },
        portalLabMovePreviewCell(
            state,
            action: PayloadAction<
                { fromRow: number; fromCell: number } & (
                    | { mode: "newRowBelow" }
                    | { mode: "newRowAt"; insertAtRow: number }
                    | { mode: "appendToRow"; toRow: number }
                    | { mode: "beforeCell"; toRow: number; toCell: number }
                )
            >
        ) {
            const page = state.portalConfig.pages.find(
                (tab) => tab.id === state.selectedPageId
            );
            if (!page) {
                return;
            }
            const rows = page.rows;
            const p = action.payload;
            const { fromRow, fromCell, mode } = p;
            if (fromRow < 0 || fromRow >= rows.length) {
                return;
            }
            const sourceRow = rows[fromRow];
            if (!sourceRow || fromCell < 0 || fromCell >= sourceRow.elements.length) {
                return;
            }
            const element = sourceRow.elements[fromCell];

            sourceRow.elements.splice(fromCell, 1);
            let removedRow = false;
            const removedRowIndex = fromRow;
            if (sourceRow.elements.length === 0) {
                rows.splice(fromRow, 1);
                removedRow = true;
            } else {
                normalizeRowSpans(sourceRow);
            }

            const adjustRow = (r: number): number => {
                if (!removedRow) {
                    return r;
                }
                if (removedRowIndex < r) {
                    return r - 1;
                }
                return r;
            };

            if (mode === "newRowBelow") {
                rows.push({
                    id: nanoid(),
                    elements: [{ ...element, span: 12 }],
                });
                return;
            }
            if (mode === "newRowAt") {
                let insertAt = Math.max(
                    0,
                    Math.min(Math.floor(p.insertAtRow), rows.length)
                );
                if (removedRow && removedRowIndex < insertAt) {
                    insertAt -= 1;
                }
                insertAt = Math.max(0, Math.min(insertAt, rows.length));
                rows.splice(insertAt, 0, {
                    id: nanoid(),
                    elements: [{ ...element, span: 12 }],
                });
                return;
            }
            if (mode === "appendToRow") {
                const tr = adjustRow(p.toRow);
                if (tr < 0 || tr >= rows.length) {
                    return;
                }
                rows[tr].elements.push(element);
                normalizeRowSpans(rows[tr]);
                return;
            }
            const origToRow = p.toRow;
            const tr = adjustRow(origToRow);
            if (tr < 0 || tr >= rows.length) {
                return;
            }
            let tc = p.toCell;
            if (!removedRow && fromRow === origToRow && fromCell < tc) {
                tc -= 1;
            }
            const targetRow = rows[tr];
            const insertAt = Math.max(0, Math.min(tc, targetRow.elements.length));
            targetRow.elements.splice(insertAt, 0, element);
            normalizeRowSpans(targetRow);
        },
        portalLabResizeAdjacentCellSpans(
            state,
            action: PayloadAction<{
                rowIndex: number;
                leftCellIndex: number;
                leftSpan: number;
                rightSpan: number;
            }>
        ) {
            const { rowIndex, leftCellIndex, leftSpan, rightSpan } = action.payload;
            const page = state.portalConfig.pages.find(
                (tab) => tab.id === state.selectedPageId
            );
            if (!page) {
                return;
            }
            if (rowIndex < 0 || rowIndex >= page.rows.length) {
                return;
            }
            const row = page.rows[rowIndex];
            if (
                !row ||
                leftCellIndex < 0 ||
                leftCellIndex >= row.elements.length - 1
            ) {
                return;
            }
            const clampedLeft = Math.min(11, Math.max(1, Math.round(leftSpan)));
            const clampedRight = Math.min(11, Math.max(1, Math.round(rightSpan)));
            const pairTotal =
                row.elements[leftCellIndex].span +
                row.elements[leftCellIndex + 1].span;
            if (clampedLeft + clampedRight !== pairTotal) {
                return;
            }
            row.elements[leftCellIndex].span = clampedLeft;
            row.elements[leftCellIndex + 1].span = clampedRight;
        },
        portalLabPatchElementConfig(
            state,
            action: PayloadAction<{ elementId: string; configPatch: Record<string, unknown> }>
        ) {
            const page = state.portalConfig.pages.find(
                (tab) => tab.id === state.selectedPageId
            );
            if (!page) {
                return;
            }
            for (const row of page.rows) {
                const el = row.elements.find((e) => e.id === action.payload.elementId);
                if (el) {
                    el.config = { ...el.config, ...action.payload.configPatch };
                    return;
                }
            }
        },
        portalLabSetSelectedLiveboard(
            state,
            action: PayloadAction<PortalLabSelectedLiveboard | null>
        ) {
            state.selectedLiveboard = action.payload;
        },
        portalLabSetLiveboardOptions(
            state,
            action: PayloadAction<{ id: string; name: string }[]>
        ) {
            state.liveboardOptions = action.payload;
        },
        resetPortalLab() {
            return {
                ...initialState,
                steps: [...initialSteps],
                portalConfig: initialPortalConfig(),
            };
        },
    },
});

export const {
    portalLabNextStep,
    portalLabPrevStep,
    portalLabSetCompletedStep,
    portalLabSetCurrentStep,
    portalLabSetDefinePortalTabIndex,
    portalLabStartFlow,
    portalLabSetIntegrationPipelines,
    portalLabSetStage,
    portalLabSetGeneratedTemplate,
    portalLabClearGeneratedTemplate,
    portalLabBeginTemplateRegeneration,
    portalLabAppendChatMessage,
    portalLabApplyChatResult,
    portalLabSetThemeMode,
    portalLabSetThemeDensity,
    portalLabSetCardStyle,
    portalLabSetElementHeight,
    portalLabSyncStageFromCompanyPortalEntity,
    portalLabSetIntegrationSelectionFromCompanyPortalEntity,
    portalLabHydrateFromCompanyPortalState,
    portalLabSetSelectedPageId,
    portalLabSetBrandKitResponse,
    portalLabShufflePreviewLogo,
    portalLabShufflePreviewLogoAndBackdrop,
    portalLabSetLogoUrl,
    portalLabSetAppLogo,
    portalLabSetBackdropUrl,
    portalLabSetCornerRadius,
    portalLabSetPortalFont,
    portalLabApplyColorTheme,
    portalLabEnsureBrandColorBaseline,
    portalLabRestoreBrandColorBaseline,
    portalLabInitPreviewColorTheme,
    portalLabAddPageTab,
    portalLabRemovePageTab,
    portalLabUpdatePageTabName,
    portalLabMovePageTab,
    portalLabSetNavigationLayout,
    portalLabSetHomePage,
    portalLabApplyTemplateToSelectedPage,
    portalLabRemovePreviewCell,
    portalLabAddPreviewBlock,
    portalLabMovePreviewCell,
    portalLabResizeAdjacentCellSpans,
    portalLabPatchElementConfig,
    portalLabSetSelectedLiveboard,
    portalLabSetLiveboardOptions,
    resetPortalLab,
} = portalLabSlice.actions;

export default portalLabSlice.reducer;
